
import { defineComponent, PropType } from 'vue'
import { DynamicInput } from '../models/dynamicInput'

export default defineComponent({
  name: 'DynamicField',

  props: {
    /** The DynamicInput object that defines the type of input to render */
    inputDefinition: {
      type: Object as PropType<DynamicInput>,
      required: true
    },

    /** The model value */
    modelValue: {
      default: ''
    }
  },

  data () {
    return {
      /** Indicates that the input has been touched */
      dirty: false,

      /** Indicates if the user's input is valid */
      isValid: true,

      /** A local copy of the modelValue */
      localValue: null as unknown as any
    }
  },

  mounted () {
    this.localValue = this.modelValue
    this.setValid()
  },

  emits: ['update:modelValue', 'update-validity'],

  computed: {
    /** The error message to be displayed if there is an error */
    errorMessage: function (): string {
      if (this.isValid) {
        return ''
      }

      if (this.inputDefinition.required && (this.localValue === '' || this.localValue === null)) {
        return 'This field is required.'
      }

      switch (this.inputDefinition.type) {
        case 'phone': return 'Please enter a valid 10 digit phone number.'
        case 'email': return 'Please enter a valid email address.'
        case 'cayuse': return 'Please enter a valid Proposal Record ID.'
        default: return ''
      }
    },

    /** The current model */
    passThrough: {
      get (): string { return this.modelValue },
      set (val) {
        this.localValue = val
        this.setValid()
        this.$emit('update:modelValue', val)
      }
    },

    showError (): boolean {
      return this.dirty && !this.isValid
    }
  },

  methods: {
    /**
     * Updates the validity flag
     */
    setValid () {
      const validity = this.validate()
      this.$emit('update-validity', validity)
      this.isValid = validity
    },

    /**
     * Returns true if the input is valid
     */
    validate (): boolean {
      switch (this.inputDefinition.type) {
        case 'email': return this.validateRequired() && this.validateEmail()
        case 'phone': return this.validateRequired() && this.validatePhone()
        case 'cayuse': return this.validateRequired() && this.validateCayuse()
        case 'checkbox group': return true
        case 'checkbox': return true
        default: return this.validateRequired()
      }
    },

    /**
     * validate Cayuse number
     */
    validateCayuse () {
      return this.localValue.length >= 1
    },

    /**
     * Returns true if the input passes the required validation
     */
    validateRequired (): boolean {
      if (this.inputDefinition.required) {
        return this.localValue !== '' && this.localValue !== null
      } else {
        return true
      }
    },

    /**
     * Returns true if the input is a valid email
     */
    validateEmail (): boolean {
      return /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/.test(this.localValue) || this.localValue === ''
    },

    /**
     * Returns true if the input is a valid phone number
     */
    validatePhone (): boolean {
      return /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/.test(this.localValue) || this.localValue === ''
    }
  }
})
